import { DEFAULT_PAGE_SIZE } from "@/modules/utils/paginated"
import { BoxLeadEntity } from "@modules/boxLead/entities/boxLeadEntity"
import { LeadColumnToSort } from "@modules/boxLead/gateway/boxLeadDto"
import { useServicesContext } from "@modules/core/services/services.context"
import useDebounce from "@modules/hooks/useDebounce"
import { ColumnSortingOrder } from "@modules/types/datatableTypes"
import { isValidSortOrder } from "@modules/ui/components/dataTables/datatableUtils"
import { usePersistedDatatable } from "@modules/utils/hooks/useDataTable"
import { useSetMultiParams } from "@modules/utils/navigation/useQueryParam"
import { useSuspenseQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router"

export const useBoxLeads = ({
  nameFilter,
  page,
  pageSize,
  sortBy,
  sortOrder,
}: {
  nameFilter?: string
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: string
}) => {
  const { boxLeadApi } = useServicesContext()

  const { data, isLoading } = useSuspenseQuery({
    queryKey: ["boxLeads", { page, pageSize, nameFilter, sortBy, sortOrder }],
    queryFn: async () => {
      if (sortBy && isValidSortBy(sortBy) && sortOrder && isValidSortOrder(sortOrder)) {
        return boxLeadApi.getBoxLeads(page, pageSize, nameFilter, sortBy, sortOrder)
      }

      return boxLeadApi.getBoxLeads(page, pageSize, nameFilter)
    },
  })

  return {
    data: data.content.map((lead) => BoxLeadEntity.fromDto(lead)) || [],
    totalElements: data.totalElements,
    isLoading,
    hasPreviousPage: !data.first,
    hasNextPage: !data.last,
  }
}

export const usePersistedBoxLeads = () => {
  const [searchParams] = useSearchParams()
  const setListParams = useSetMultiParams()

  const sortBy = searchParams.get("sortBy") ?? undefined
  const sortOrder = searchParams.get("order") ?? undefined
  const nameFilter = searchParams.get("nameFilter") ?? undefined

  const debouncedSearchTerm = useDebounce(nameFilter, 300)

  const { page, setPage, size: pageSize, setSize: setPageSize } = usePersistedDatatable(DEFAULT_PAGE_SIZE)

  const { data, isLoading, hasPreviousPage, hasNextPage, totalElements } = useBoxLeads({
    nameFilter: debouncedSearchTerm,
    page,
    pageSize,
    sortBy,
    sortOrder,
  })

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1)
    }
  }

  const setSorting = (field: LeadColumnToSort, order: ColumnSortingOrder) => {
    if (field === sortBy && order === sortOrder) {
      setListParams([
        { key: "sortBy", value: undefined },
        { key: "order", value: undefined },
      ])
    } else {
      setListParams([
        { key: "sortBy", value: field },
        { key: "order", value: order },
      ])
    }
  }

  return {
    data,
    totalElements,
    isLoading,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    setSorting,
    sortBy,
    sortOrder,
    isSearching: !!nameFilter,
  }
}

function isValidSortBy(sortBy: string): sortBy is LeadColumnToSort {
  return Object.values(LeadColumnToSort).includes(sortBy as LeadColumnToSort)
}
